import { useEffect, useRef, useState } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
} from "chart.js";
import { ArrowLeft } from "@mui/icons-material";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  PointElement
);

// Screen breakpoints
const SCREEN_BREAKPOINT_SMALL = 600;
const SCREEN_BREAKPOINT_MEDIUM = 800;

// Chart panel dimensions
const CHART_WIDTH_MOBILE = 90;
const CHART_WIDTH_TABLET = 50;
const CHART_WIDTH_DESKTOP = 40;
const CHART_MIN_WIDTH_MOBILE = 50;
const CHART_MIN_WIDTH_DESKTOP = 25;
const CHART_MAX_WIDTH_MOBILE = 90;
const CHART_MAX_WIDTH_DESKTOP = 43;
const CHART_MIN_HEIGHT = 20;
const CHART_MAX_HEIGHT = 97;

// Font sizes
const FONT_SIZE_SMALL = 9;
const FONT_SIZE_MEDIUM = 12;
const FONT_SIZE_LARGE = 14;
const FONT_SIZE_TINY = 10;

// Padding values
const PADDING_SMALL = 5;
const PADDING_MEDIUM = 8;
const PADDING_LARGE = 10;
const PADDING_MINI = 2;
const PADDING_LABEL = 3;

// Chart configuration
const CHART_SUGGESTED_MAX = 500;
const CHART_STEP_SIZE = 100;
const CHART_ANIMATION_DURATION = 800;

// Level thresholds configuration
const BASE_XP = 100;
const MAX_LEVEL = 5;

// Calculate level threshold using formula: BASE_XP * 2^(level-1)
const getLevelThreshold = (level) => BASE_XP * Math.pow(2, level - 1);

// Generate level thresholds dynamically
const LEVEL_THRESHOLDS = Array.from({ length: MAX_LEVEL }, (_, i) => ({
  level: i + 1,
  threshold: getLevelThreshold(i + 1),
  label: `L${i + 1}`
}));

// Colors
const PRIMARY_COLOR = "#04a5a6";
const TEXT_COLOR = "#333333";
const GRID_COLOR = "rgba(4, 165, 166, 0.2)";
const ANGLE_LINE_COLOR = "rgba(4, 165, 166, 0.15)";
const LABEL_BG_COLOR = "rgba(255, 255, 255, 0.95)";
const LABEL_BORDER_COLOR = "rgba(4, 165, 166, 0.8)";
const TOOLTIP_BG_COLOR = "rgba(4, 165, 166, 0.95)";
const TICK_BACKDROP_COLOR = "rgba(255, 255, 255, 0.9)";
const WHITE_COLOR = "#fff";
const BLACK_COLOR = "#000000";

// Label dimensions
const LABEL_BG_HEIGHT_MOBILE = 18;
const LABEL_BG_HEIGHT_DESKTOP = 22;
const LABEL_BORDER_RADIUS = 4;
const LABEL_MAX_LINE_LENGTH_MOBILE = 10;
const LABEL_MAX_LINE_LENGTH_DESKTOP = 12;
const LABEL_MAX_LINES = 2;
const LABEL_ELLIPSIS_OFFSET = 3;

// Timing
const AUTO_HIDE_DELAY = 5000;
const LABEL_OFFSET = 15;

export default function ChartPanel(props) {
  const { styles, data, showNextQuestionBtn, chartPanel, setChartPanel } = props;

  const animationFrameId = useRef(null);
  const chartRef = useRef(null);
  const chartWrapperRef = useRef(null);
  const hideTimerRef = useRef(null);

  const [chartDimensions, setChartDimensions] = useState({
    width: window.innerWidth <= SCREEN_BREAKPOINT_SMALL ? CHART_WIDTH_MOBILE : window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? CHART_WIDTH_TABLET : CHART_WIDTH_DESKTOP,
  });
  const [showDataLabels, setShowDataLabels] = useState(true);

  const toggleChartPanel = () => {
    setChartPanel(!chartPanel);
  };

  const resizePanelMouse = (e) => {
    handleResize(e.clientX, e.clientY);
  };

  const resizePanelTouch = (e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleResize(touch.clientX, touch.clientY);
    }
  };

  const handleResize = (clientX, clientY) => {
    if (!chartWrapperRef.current) return;

    if (animationFrameId.current) return; // skip if frame already scheduled

    animationFrameId.current = requestAnimationFrame(() => {
      animationFrameId.current = null;

      const newWidth = Math.max(
        window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? CHART_MIN_WIDTH_MOBILE : CHART_MIN_WIDTH_DESKTOP,
        Math.min(
          ((window.innerWidth - clientX) / window.innerWidth) * 100,
          window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? CHART_MAX_WIDTH_MOBILE : CHART_MAX_WIDTH_DESKTOP
        )
      );
      const newHeight = Math.max(
        CHART_MIN_HEIGHT,
        Math.min(
          ((window.innerHeight - clientY) / window.innerHeight) * 100,
          CHART_MAX_HEIGHT
        )
      );

      setChartDimensions({ width: newWidth, height: newHeight });
    });
  };

  const startResizing = (e) => {
    e.preventDefault();
    document.addEventListener("mousemove", resizePanelMouse);
    document.addEventListener("mouseup", stopResizing);

    document.addEventListener("touchmove", resizePanelTouch, {
      passive: false,
    });
    document.addEventListener("touchend", stopResizing);
  };

  const stopResizing = () => {
    document.removeEventListener("mousemove", resizePanelMouse);
    document.removeEventListener("mouseup", stopResizing);

    document.removeEventListener("touchmove", resizePanelTouch);
    document.removeEventListener("touchend", stopResizing);
  };

  useEffect(() => {
    const chart = chartRef.current;
    const canvas = chart?.canvas;

    const handleClick = (event) => {
      const points = chart.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: false },
        false
      );
      if (points.length > 0) {
        chart.tooltip.setActiveElements(points, { x: event.x, y: event.y });
        chart.update();
      }
    };

    canvas?.addEventListener("click", handleClick);
    return () => canvas?.removeEventListener("click", handleClick);
  }, []);

  // Auto-hide labels after 5 seconds
  useEffect(() => {
    if (chartPanel && data?.labels?.length > 0) {
      setShowDataLabels(true);
      if (chartRef.current) {
        chartRef.current.showDataLabels = true;
      }
      
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      
      hideTimerRef.current = setTimeout(() => {
        setShowDataLabels(false);
        if (chartRef.current) {
          chartRef.current.showDataLabels = false;
          chartRef.current.update();
        }
      }, AUTO_HIDE_DELAY);
    }
    
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [chartPanel, data]);

  const handleChartMouseEnter = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    setShowDataLabels(true);
    if (chartRef.current) {
      chartRef.current.showDataLabels = true;
      chartRef.current.update();
    }
  };

  const handleChartMouseLeave = () => {
    setShowDataLabels(false);
    if (chartRef.current) {
      chartRef.current.showDataLabels = false;
      chartRef.current.update();
    }
  };

  return (
    <div
      ref={chartWrapperRef}
      className={`${styles.chart_panel} ${chartPanel && styles.open}`}
      style={{
        height: `${chartDimensions.width}vw`,
        width: `${chartPanel ? `${chartDimensions.width}vw` : "0%"}`,
      }}
    >
      <div
        className={`${styles.collapse_btn} ${!chartPanel && styles.close} ${
          showNextQuestionBtn && styles.animate
        }`}
        onClick={toggleChartPanel}
      >
        <ArrowLeft className={styles.icon} />
      </div>
      <Radar
        className={styles.chart}
        data={data}
        ref={chartRef}
        onMouseEnter={handleChartMouseEnter}
        onMouseLeave={handleChartMouseLeave}
        options={{
          events: ["click"],
          showTooltip: false,
          layout: {
            padding: {
              top: PADDING_LARGE,
              bottom: PADDING_LARGE,
              left: PADDING_LARGE,
              right: PADDING_LARGE,
            },
          },
          animation: {
            duration: CHART_ANIMATION_DURATION,
            easing: "easeInOutQuart",
            onComplete: function (context) {
              const chartInstance = context.chart;
              if (!chartInstance.showDataLabels) return;
              
              var ctx = context.chart.ctx;
              ctx.font = `600 ${window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? FONT_SIZE_SMALL : FONT_SIZE_MEDIUM}px poppins`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";

              if (window.innerWidth <= SCREEN_BREAKPOINT_SMALL) return;
              context.chart._metasets.forEach(function (dataset) {
                for (var i = 0; i < dataset.data.length; i++) {
                  var model = dataset.data[i];
                  const value = dataset.data[i]?.$context.raw;
                  if (value > 0) {
                    // Draw background for label
                    const text = value + " XP";
                    const textWidth = ctx.measureText(text).width;
                    const padding = PADDING_LABEL;
                    const bgHeight = window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? LABEL_BG_HEIGHT_MOBILE : LABEL_BG_HEIGHT_DESKTOP;
                    
                    ctx.fillStyle = LABEL_BG_COLOR;
                    ctx.strokeStyle = LABEL_BORDER_COLOR;
                    // ctx.lineWidth = 0.5;
                    
                    const rectX = model.x - textWidth / 2 - padding;
                    const rectY = model.y - bgHeight / 2 - LABEL_OFFSET;
                    const rectWidth = textWidth + padding * 2;
                    
                    ctx.beginPath();
                    ctx.roundRect(rectX, rectY, rectWidth, bgHeight, LABEL_BORDER_RADIUS);
                    ctx.fill();
                    // ctx.stroke();
                    
                    // Draw text
                    ctx.fillStyle = PRIMARY_COLOR;
                    ctx.fillText(text, model.x, model.y - LABEL_OFFSET);
                  }
                }
              });
            },
          },
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            r: {
              beginAtZero: true,
              suggestedMax: CHART_SUGGESTED_MAX,
              pointLabels: {
                font: {
                  size: window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? FONT_SIZE_SMALL : FONT_SIZE_LARGE,
                  family: "poppins",
                  weight: "600",
                },
                color: TEXT_COLOR,
                padding: window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? PADDING_SMALL : PADDING_MEDIUM,
                callback: function(label) {
                  // Capitalize and wrap text for multiline display
                  const capitalized = label.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');
                  
                  const maxLineLength = window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? LABEL_MAX_LINE_LENGTH_MOBILE : LABEL_MAX_LINE_LENGTH_DESKTOP;
                  const words = capitalized.split(' ');
                  const lines = [];
                  let currentLine = '';
                  
                  words.forEach(word => {
                    if ((currentLine + ' ' + word).trim().length <= maxLineLength) {
                      currentLine = (currentLine + ' ' + word).trim();
                    } else {
                      if (currentLine) lines.push(currentLine);
                      currentLine = word;
                    }
                  });
                  if (currentLine) lines.push(currentLine);
                  
                  // Limit to 2 lines max
                  if (lines.length > LABEL_MAX_LINES) {
                    return [lines[0], lines[1].substring(0, maxLineLength - LABEL_ELLIPSIS_OFFSET) + '...'];
                  }
                  return lines;
                },
              },
              angleLines: {
                display: true,
                color: ANGLE_LINE_COLOR,
                lineWidth: 1,
              },
              grid: {
                color: GRID_COLOR,
                lineWidth: 1,
              },
              ticks: {
                stepSize: CHART_STEP_SIZE,
                color: BLACK_COLOR,
                font: {
                  size: window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? FONT_SIZE_SMALL : FONT_SIZE_TINY,
                  family: "poppins",
                  weight: "600",
                },
                backdropPadding: PADDING_LABEL,
                backdropColor: TICK_BACKDROP_COLOR,
                showLabelBackdrop: true,
                callback: (value) => {
                  // Show level labels only at level threshold values
                  if (value === 0) return "";
                  const levelData = LEVEL_THRESHOLDS.find(l => l.threshold === value);
                  return levelData ? levelData.label : "";
                },
              },
            },
          },
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                font: {
                  size: window.innerWidth <= SCREEN_BREAKPOINT_MEDIUM ? FONT_SIZE_TINY : FONT_SIZE_LARGE,
                  family: "poppins",
                  weight: "600",
                },
                color: PRIMARY_COLOR,
                padding: PADDING_MINI,
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
            tooltip: {
              enabled: true,
              bodyFont: {
                weight: "600",
              },
              titleFont: {
                weight: "600",
              },
              backgroundColor: TOOLTIP_BG_COLOR,
              titleColor: WHITE_COLOR,
              bodyColor: WHITE_COLOR,
              borderColor: PRIMARY_COLOR,
              borderWidth: 1,
              padding: PADDING_SMALL,
              displayColors: true,
              callbacks: {
                label: function(context) {
                  return `${context.dataset.label}: ${context.parsed.r}`;
                },
              },
            },
          },
        }}
      />
      <div
        className={styles.resize_btn}
        onMouseDown={startResizing}
        onTouchStart={startResizing}
      >
        <ArrowLeft className={styles.icon} />
      </div>
    </div>
  );
}
